import React from 'react';
import { Info, Type, FileText, Eye, BookOpen, BookmarkCheck, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о размерах текста, типографике и нормативах
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о размерах текста: правила, нормативы, рекомендации
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Type className="w-5 h-5 text-indigo-500" />
          Что такое типографика и почему она важна?
        </h3>
        <p>
          <strong>Типографика</strong> — это искусство и техника оформления текста для эффективной передачи информации. Грамотно подобранные шрифты, размеры и интервалы существенно влияют на восприятие текста, удобочитаемость и доступность контента.
        </p>
        <p>
          В цифровую эпоху правильная типографика стала ещё более важной, так как люди потребляют огромное количество текстовой информации с разных устройств. Хорошо оформленный текст:
        </p>
        <ul>
          <li>Снижает утомляемость при чтении</li>
          <li>Улучшает понимание и запоминание информации</li>
          <li>Делает контент доступным для людей с нарушениями зрения</li>
          <li>Создает профессиональное впечатление и укрепляет доверие</li>
          <li>Соответствует законодательным требованиям (для государственных и социально значимых ресурсов)</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Единицы измерения текста
        </h3>
        <p>
          В веб-разработке и печати используются различные единицы измерения размера текста, каждая со своими особенностями и областью применения:
        </p>
        <dl>
          <dt><strong>Пиксели (px)</strong></dt>
          <dd>Абсолютные единицы для экранов. 1 пиксель соответствует одной точке на экране. Наиболее распространенная единица в веб-дизайне.</dd>
          
          <dt><strong>Пункты (pt)</strong></dt>
          <dd>Традиционные единицы для печати. 1 пункт = 1/72 дюйма. Стандарт для печатной продукции и текстовых редакторов.</dd>
          
          <dt><strong>Em (em)</strong></dt>
          <dd>Относительные единицы, равные текущему размеру шрифта элемента. Например, 1.5em означает полуторакратный размер от текущего.</dd>
          
          <dt><strong>Rem (rem)</strong></dt>
          <dd>Относительные единицы, привязанные к размеру шрифта корневого элемента (обычно 16px). Обеспечивают более предсказуемое масштабирование.</dd>
          
          <dt><strong>Проценты (%)</strong></dt>
          <dd>Указывают размер относительно родительского элемента.</dd>
          
          <dt><strong>Viewport Width (vw)</strong></dt>
          <dd>Размер в процентах от ширины области просмотра. 1vw = 1% ширины экрана.</dd>
        </dl>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Рекомендации по выбору единиц измерения
          </h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li><strong>Для веб-сайтов:</strong> rem для основного текста (обеспечивает доступность и масштабируемость), em для относительных размеров компонентов</li>
            <li><strong>Для мобильных приложений:</strong> sp (scaled pixels) в Android, pt в iOS</li>
            <li><strong>Для печати:</strong> pt (пункты) для всех типов текста</li>
            <li><strong>Для адаптивного дизайна:</strong> комбинация rem и vw с ограничениями min/max</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-500" />
          Российские нормативы и стандарты
        </h3>
        <p>
          В России действуют строгие нормативы, регулирующие оформление текста в различных сферах. Соблюдение этих стандартов особенно важно для государственных организаций, образовательных учреждений и социально значимых ресурсов.
        </p>
        
        <h4>Ключевые нормативные документы:</h4>
        <ul>
          <li>
            <strong>ГОСТ Р 58511-2019</strong> «Издания. Требования к шрифтовому оформлению» — устанавливает требования к оформлению печатных и электронных изданий, включая минимальные размеры шрифтов.
          </li>
          <li>
            <strong>ГОСТ Р 54723-2019</strong> «Интернет-ресурсы и другая информация, представленная в электронно-цифровой форме» — определяет требования к оформлению электронных документов и веб-сайтов.
          </li>
          <li>
            <strong>ГОСТ Р 57850-2017</strong> «Оформление сайтов для слабовидящих» — устанавливает специфические требования к версиям сайтов для пользователей с нарушениями зрения.
          </li>
          <li>
            <strong>СанПиН 1.2.3685-21</strong> «Гигиенические нормативы и требования к обеспечению безопасности и (или) безвредности для человека факторов среды обитания» — включает требования к оформлению электронных изданий для детей и подростков.
          </li>
        </ul>

        <h4>Основные требования по ГОСТ Р 57850-2017 (версии для слабовидящих):</h4>
        <ul>
          <li>Минимальный размер шрифта — 18px</li>
          <li>Высота строки (интерлиньяж) — не менее 1.5</li>
          <li>Контрастность текста и фона — не менее 7:1</li>
          <li>Возможность увеличения шрифта до 200%</li>
          <li>Отсутствие светлого текста на светлом фоне</li>
          <li>Использование шрифтов без засечек (sans-serif)</li>
          <li>Отсутствие переносов слов</li>
        </ul>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Юридическая ответственность
          </h4>
          <p className="text-amber-600 dark:text-amber-400 text-sm mb-0">
            Согласно законодательству РФ, несоблюдение требований к версии сайта для слабовидящих может повлечь административную ответственность в виде штрафов:
            <br />
            • для должностных лиц — от 10 000 до 30 000 рублей;
            <br />
            • для юридических лиц — от 100 000 до 300 000 рублей.
            <br />
            Это относится к сайтам государственных органов, организаций социальной сферы и компаний, предоставляющих услуги населению.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <BookmarkCheck className="w-5 h-5 text-indigo-500" />
          Рекомендации по оформлению текста для разных целевых аудиторий
        </h3>

        <h4>Для общей аудитории:</h4>
        <ul>
          <li>Основной текст: 16-18px для веб, 10-12pt для печати</li>
          <li>Высота строки (интерлиньяж): 1.5-1.6</li>
          <li>Шрифты: sans-serif для экранов, serif для длинных печатных текстов</li>
          <li>Длина строки: 60-80 символов (около 600px)</li>
        </ul>

        <h4>Для пожилых людей:</h4>
        <ul>
          <li>Основной текст: не менее 18px</li>
          <li>Высокая контрастность: темный текст на светлом фоне</li>
          <li>Простые, четкие шрифты без декоративных элементов</li>
          <li>Увеличенные интервалы между строками (не менее 1.5)</li>
          <li>Без переносов слов и выравнивания по ширине</li>
        </ul>

        <h4>Для детей и подростков:</h4>
        <ul>
          <li>Для дошкольников: 18-20px (экран), 16-18pt (печать)</li>
          <li>Для младших школьников: 16-18px (экран), 14-16pt (печать)</li>
          <li>Для подростков: 14-16px (экран), 12-14pt (печать)</li>
          <li>Простые шрифты с четкими округлыми формами</li>
          <li>Увеличенное расстояние между строками и буквами</li>
        </ul>

        <h4>Для людей с дислексией:</h4>
        <ul>
          <li>Шрифты, специально разработанные для дислексии (OpenDyslexic, Dyslexie)</li>
          <li>Увеличенный межстрочный интервал (1.8-2.0)</li>
          <li>Увеличенное межбуквенное расстояние (0.05-0.1em)</li>
          <li>Короткие абзацы и разбивка длинных текстов</li>
          <li>Выравнивание текста по левому краю (никогда по ширине)</li>
        </ul>

        <h3>Адаптивная типографика</h3>
        <p>
          В современном мире пользователи просматривают контент на разных устройствах — от маленьких смартфонов до больших телевизионных экранов. Адаптивная типографика обеспечивает оптимальное отображение текста на всех устройствах:
        </p>
        <ul>
          <li>Используйте относительные единицы (rem, em, vw) вместо абсолютных (px)</li>
          <li>Применяйте медиа-запросы для корректировки размеров шрифта на разных экранах</li>
          <li>Уменьшайте длину строки на маленьких экранах</li>
          <li>Увеличивайте размеры интерактивных элементов для сенсорных устройств</li>
          <li>Используйте технику "fluid typography" для плавного масштабирования между точками перехода</li>
        </ul>
        
        <h3>Заключение</h3>
        <p>
          Правильно подобранный размер текста и типографика — не просто вопрос эстетики, а важный компонент доступности, удобства использования и соответствия нормативным требованиям. Особенно в России, где требования к версиям для слабовидящих строго регламентированы.
        </p>
        <p>
          Используйте наш калькулятор размера текста для оптимизации типографики ваших проектов, соблюдения нормативов и создания по-настоящему инклюзивного дизайна, доступного всем пользователям независимо от их особенностей и устройств, которыми они пользуются.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;